import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, ShieldCheck, Lock, Scale } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const SECTIONS = [
  {
    id: 'educational',
    icon: GraduationCap,
    title: 'Educational disclaimer',
    body:
      "NestWise provides curriculum-supportive learning materials for home educators. It does not replace professional teaching advice. Parents and guardians are responsible for their child's overall education programme and MoE exemption compliance.",
  },
  {
    id: 'accuracy',
    icon: ShieldCheck,
    title: 'Accuracy disclaimer',
    body:
      'While every effort is made to ensure content accuracy and NZC alignment, NestWise makes no guarantee that content is error-free. We welcome corrections and feedback.',
  },
  {
    id: 'privacy',
    icon: Lock,
    title: 'Privacy statement',
    body:
      'We collect only the information needed to provide our service. We do not sell or share personal data. Child data is handled with the highest care in accordance with the NZ Privacy Act 2020.',
  },
  {
    id: 'consumer',
    icon: Scale,
    title: 'Consumer rights',
    body:
      'Our products come with guarantees under the Consumer Guarantees Act 1993 that cannot be excluded.',
  },
];

export default function LegalPage() {
  useEffect(() => {
    // Scroll to hash anchor if present (e.g. /legal#privacy)
    const hash = window.location.hash?.replace('#', '');
    if (hash) {
      const el = document.getElementById(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="legal-page">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <BrandMark size={36} />
          </Link>
          <Link to="/" data-testid="legal-back-link">
            <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back</span>
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-fern font-semibold mb-3">
            Terms &amp; Disclaimers
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-fern-dark tracking-tight">
            The fine print, in plain English
          </h1>
          <p className="text-slate-600 text-base sm:text-lg mt-3 max-w-2xl">
            We believe whānau deserve clarity — here is how NestWise fits into your homeschool journey,
            and the rights you have when using it.
          </p>
        </div>

        <div className="space-y-5">
          {SECTIONS.map(({ id, icon: Icon, title, body }) => (
            <Card
              key={id}
              id={id}
              data-testid={`legal-section-${id}`}
              className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden scroll-mt-24"
            >
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-fern/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-fern" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading text-xl font-semibold text-fern-dark mb-2">
                      {title}
                    </h2>
                    <p className="text-slate-700 text-base leading-relaxed">{body}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 rounded-3xl border border-fern/15 bg-fern/5 px-6 py-5 text-sm text-slate-600">
          <p>
            Questions, corrections, or feedback? Email us at{' '}
            <a
              href="mailto:kia-ora@nestwise.nz"
              className="text-fern font-semibold hover:underline"
              data-testid="legal-contact-email"
            >
              kia-ora@nestwise.nz
            </a>{' '}
            — we read every note.
          </p>
        </div>
      </main>
    </div>
  );
}
